import { useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, query, where, writeBatch } from "firebase/firestore";
import { LoaderCircle, Plus, Trash2 } from "lucide-react";
import type { AvailabilityRule, Chair, Practitioner, Service } from "@cop/domain";
import { db, TENANT } from "../lib/firebase";
import { useLive } from "../lib/data";

const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const toTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const toMin = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const slug = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/^(dra?|dr)\.?\s+/, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);

type Range = { weekday: number; start: string; end: string; chairId: string };
type Draft = Pick<Practitioner, "displayName" | "title" | "license" | "color" | "active" | "bookableOnline" | "serviceIds"> & { id: string | null; ranges: Range[] };

const empty = (): Draft => ({ id: null, displayName: "", title: "", license: "", color: "#349B97", active: true, bookableOnline: true, serviceIds: [], ranges: [] });

/** Managers edit who attends, which treatments each person offers and the weekly schedule. Booking reads it live. */
export function TeamSettings() {
  const [tab, setTab] = useState<"team" | "services">("team");
  return (
    <div className="page">
      <header className="toolbar">
        <div className="toolbar-title">
          <h1>Profesionales y agenda</h1>
          <p className="muted small">Quién atiende, qué tratamientos hace cada profesional y en qué días y horarios. La reserva online usa estos datos al instante.</p>
        </div>
        <div className="segmented" role="tablist">
          <button role="tab" aria-selected={tab === "team"} onClick={() => setTab("team")}>
            Profesionales
          </button>
          <button role="tab" aria-selected={tab === "services"} onClick={() => setTab("services")}>
            Tratamientos
          </button>
        </div>
      </header>
      {tab === "team" ? <PractitionersEditor /> : <ServicesEditor />}
    </div>
  );
}

function PractitionersEditor() {
  const { data: practitioners } = useLive<Practitioner>("practitioners", []);
  const { data: services } = useLive<Service>("services", []);
  const { data: chairs } = useLive<Chair>("chairs", []);
  const [selected, setSelected] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const list = useMemo(() => (practitioners ?? []).filter((p) => !p.placeholder).sort((a, b) => Number(b.active) - Number(a.active) || a.displayName.localeCompare(b.displayName)), [practitioners]);
  const activeServices = (services ?? []).filter((s) => s.active).sort((a, b) => a.name.localeCompare(b.name));
  const chairList = (chairs ?? []).filter((c) => c.active !== false);

  useEffect(() => {
    if (selected === null && list[0]) setSelected(list[0].id);
  }, [list, selected]);

  useEffect(() => {
    setMsg(null);
    if (selected === "new") return setDraft({ ...empty(), ranges: [] });
    const p = list.find((x) => x.id === selected);
    if (!p) return setDraft(null);
    let alive = true;
    getDocs(query(collection(db, "tenants", TENANT, "availabilityRules"), where("practitionerId", "==", p.id))).then((s) => {
      if (!alive) return;
      const ranges = s.docs
        .map((d) => d.data() as AvailabilityRule)
        .sort((a, b) => a.weekday - b.weekday || a.startMinutes - b.startMinutes)
        .map((r) => ({ weekday: r.weekday, start: toTime(r.startMinutes), end: toTime(r.endMinutes), chairId: r.chairId }));
      setDraft({ id: p.id, displayName: p.displayName, title: p.title, license: p.license ?? "", color: p.color, active: p.active, bookableOnline: p.bookableOnline, serviceIds: p.serviceIds, ranges });
    });
    return () => {
      alive = false;
    };
  }, [selected, list]);

  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setRange = (i: number, patch: Partial<Range>) => set({ ranges: draft!.ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  async function save() {
    if (!draft) return;
    const name = draft.displayName.trim();
    if (!name) return setMsg({ kind: "error", text: "Poné el nombre del profesional." });
    const bad = draft.ranges.find((r) => toMin(r.end) <= toMin(r.start));
    if (bad) return setMsg({ kind: "error", text: `Revisá la franja del ${DAYS[bad.weekday - 1]?.toLowerCase()}: termina antes de empezar.` });
    const id = draft.id ?? slug(name);
    if (!id) return setMsg({ kind: "error", text: "El nombre no sirve para crear el profesional." });
    if (!draft.id && list.some((p) => p.id === id)) return setMsg({ kind: "error", text: "Ya existe un profesional con ese nombre." });
    setBusy(true);
    setMsg(null);
    try {
      const base = doc(db, "tenants", TENANT, "practitioners", id);
      const old = await getDocs(query(collection(db, "tenants", TENANT, "availabilityRules"), where("practitionerId", "==", id)));
      const b = writeBatch(db);
      b.set(
        base,
        { displayName: name, title: draft.title.trim(), license: draft.license?.trim() || null, color: draft.color, active: draft.active, bookableOnline: draft.bookableOnline, serviceIds: draft.serviceIds, specialties: [], placeholder: false },
        { merge: true },
      );
      old.docs.forEach((d) => b.delete(d.ref));
      draft.ranges.forEach((r, i) =>
        b.set(doc(db, "tenants", TENANT, "availabilityRules", `${id}-${r.weekday}-${i}`), {
          practitionerId: id,
          chairId: r.chairId,
          weekday: r.weekday,
          startMinutes: toMin(r.start),
          endMinutes: toMin(r.end),
          branchId: "pilar",
          validFrom: null,
          validTo: null,
        }),
      );
      await b.commit();
      setSelected(id);
      setMsg({ kind: "ok", text: "Guardado. La reserva online ya usa estos datos." });
    } catch (e) {
      setMsg({ kind: "error", text: (e as Error).message.includes("permission") ? "No tenés permisos para editar la agenda." : "No se pudo guardar. Probá de nuevo." });
    } finally {
      setBusy(false);
    }
  }

  if (!practitioners || !services) return <div className="skeleton-rows" aria-busy="true" />;

  return (
    <div className="settings-split">
      <aside className="settings-list" aria-label="Profesionales">
        {list.map((p) => (
          <button key={p.id} className="settings-item" aria-current={selected === p.id || undefined} onClick={() => setSelected(p.id)}>
            <span className="settings-dot" style={{ background: p.color }} aria-hidden />
            <span>
              <strong>{p.displayName}</strong>
              <span className="muted small">{p.active ? p.title || "Sin área" : "Inactivo"}</span>
            </span>
          </button>
        ))}
        <button className="settings-item settings-add" aria-current={selected === "new" || undefined} onClick={() => setSelected("new")}>
          <Plus size={16} aria-hidden /> Nuevo profesional
        </button>
      </aside>

      {draft && (
        <section className="settings-editor" aria-label="Editar profesional">
          <div className="settings-grid">
            <label className="field">
              <span>Nombre visible</span>
              <input value={draft.displayName} onChange={(e) => set({ displayName: e.target.value })} placeholder="Dra. Nombre Apellido" />
            </label>
            <label className="field">
              <span>Área</span>
              <input value={draft.title} onChange={(e) => set({ title: e.target.value })} placeholder="Odontología general" />
            </label>
            <label className="field">
              <span>Matrícula</span>
              <input value={draft.license ?? ""} onChange={(e) => set({ license: e.target.value })} placeholder="M.P. 00000" />
            </label>
            <label className="field">
              <span>Color en la agenda</span>
              <input type="color" value={draft.color} onChange={(e) => set({ color: e.target.value })} />
            </label>
          </div>
          <div className="row gap-16 wrap">
            <label className="check">
              <input type="checkbox" checked={draft.active} onChange={(e) => set({ active: e.target.checked })} /> Activo
            </label>
            <label className="check">
              <input type="checkbox" checked={draft.bookableOnline} onChange={(e) => set({ bookableOnline: e.target.checked })} /> Se puede reservar online
            </label>
          </div>

          <fieldset className="settings-fieldset">
            <legend>Tratamientos que atiende</legend>
            <div className="settings-checks">
              {activeServices.map((s) => (
                <label key={s.id} className="check">
                  <input
                    type="checkbox"
                    checked={draft.serviceIds.includes(s.id)}
                    onChange={(e) => set({ serviceIds: e.target.checked ? [...draft.serviceIds, s.id] : draft.serviceIds.filter((x) => x !== s.id) })}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="settings-fieldset">
            <legend>Horario semanal</legend>
            {draft.ranges.length === 0 && <p className="muted small">Sin horarios: no aparece en la reserva online.</p>}
            <ul className="settings-ranges">
              {draft.ranges.map((r, i) => (
                <li key={i}>
                  <select value={r.weekday} onChange={(e) => setRange(i, { weekday: Number(e.target.value) })} aria-label="Día">
                    {DAYS.map((d, j) => (
                      <option key={d} value={j + 1}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <input type="time" step={900} value={r.start} onChange={(e) => setRange(i, { start: e.target.value })} aria-label="Desde" />
                  <span className="muted small">a</span>
                  <input type="time" step={900} value={r.end} onChange={(e) => setRange(i, { end: e.target.value })} aria-label="Hasta" />
                  <select value={r.chairId} onChange={(e) => setRange(i, { chairId: e.target.value })} aria-label="Sillón">
                    {chairList.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <button className="icon-btn" onClick={() => set({ ranges: draft.ranges.filter((_, j) => j !== i) })} aria-label="Quitar franja" title="Quitar franja">
                    <Trash2 size={16} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
            <button className="btn btn-sm" onClick={() => set({ ranges: [...draft.ranges, { weekday: 1, start: "08:00", end: "18:00", chairId: chairList[0]?.id ?? "sillon-1" }] })}>
              <Plus size={14} aria-hidden /> Agregar franja
            </button>
          </fieldset>

          {msg && <p className={msg.kind === "error" ? "notice notice-danger" : "notice"}>{msg.text}</p>}
          <div className="row gap-8">
            <button className="btn btn-primary" onClick={save} disabled={busy}>
              {busy && <LoaderCircle className="spin" size={16} aria-hidden />} {draft.id ? "Guardar cambios" : "Crear profesional"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function ServicesEditor() {
  const { data: services } = useLive<Service>("services", []);
  const [rows, setRows] = useState<Record<string, Service>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (services) setRows(Object.fromEntries(services.map((s) => [s.id, s])));
  }, [services]);

  const sorted = Object.values(rows).sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
  const patch = (id: string, p: Partial<Service>) => setRows((r) => ({ ...r, [id]: { ...r[id]!, ...p } }));

  async function save(s: Service) {
    setBusy(s.id);
    setMsg(null);
    try {
      const b = writeBatch(db);
      b.set(doc(db, "tenants", TENANT, "services", s.id), { name: s.name.trim(), durationMinutes: Number(s.durationMinutes), bufferMinutes: Number(s.bufferMinutes), bookableOnline: s.bookableOnline, active: s.active }, { merge: true });
      await b.commit();
      setMsg(`«${s.name}» guardado.`);
    } catch {
      setMsg("No se pudo guardar. Revisá tus permisos.");
    } finally {
      setBusy(null);
    }
  }

  const [newName, setNewName] = useState("");
  async function add(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    const id = slug(name);
    if (!id || rows[id]) return setMsg("Ya existe un tratamiento con ese nombre.");
    const b = writeBatch(db);
    b.set(doc(db, "tenants", TENANT, "services", id), { name, family: "diagnostico", summary: "", durationMinutes: 45, bufferMinutes: 10, bookableOnline: true, requiresEvaluation: false, active: true });
    try {
      await b.commit();
      setNewName("");
      setMsg(`«${name}» creado. Asignalo a un profesional para que aparezca en la reserva.`);
    } catch {
      setMsg("No se pudo crear. Revisá tus permisos.");
    }
  }

  if (!services) return <div className="skeleton-rows" aria-busy="true" />;
  return (
    <div className="settings-services">
      <p className="muted small">La duración es el bloque que se reserva en la agenda; el asistente y recepción pueden ajustarlo según el caso.</p>
      {msg && <p className="notice">{msg}</p>}
      <table className="settings-table">
        <thead>
          <tr>
            <th>Tratamiento</th>
            <th>Duración</th>
            <th>Margen</th>
            <th>Online</th>
            <th>Activo</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {sorted.map((s) => (
            <tr key={s.id} data-inactive={!s.active || undefined}>
              <td>
                <input value={s.name} onChange={(e) => patch(s.id, { name: e.target.value })} aria-label="Nombre" />
              </td>
              <td>
                <input type="number" min={10} step={5} value={s.durationMinutes} onChange={(e) => patch(s.id, { durationMinutes: Number(e.target.value) })} aria-label="Duración en minutos" /> min
              </td>
              <td>
                <input type="number" min={0} step={5} value={s.bufferMinutes} onChange={(e) => patch(s.id, { bufferMinutes: Number(e.target.value) })} aria-label="Margen en minutos" /> min
              </td>
              <td>
                <input type="checkbox" checked={s.bookableOnline} onChange={(e) => patch(s.id, { bookableOnline: e.target.checked })} aria-label="Reservable online" />
              </td>
              <td>
                <input type="checkbox" checked={s.active} onChange={(e) => patch(s.id, { active: e.target.checked })} aria-label="Activo" />
              </td>
              <td>
                <button className="btn btn-sm" onClick={() => save(s)} disabled={busy === s.id}>
                  {busy === s.id ? <LoaderCircle className="spin" size={14} aria-hidden /> : "Guardar"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <form className="row gap-8" onSubmit={add}>
        <input className="settings-new" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nuevo tratamiento" aria-label="Nombre del nuevo tratamiento" />
        <button className="btn btn-sm" disabled={!newName.trim()}>
          <Plus size={14} aria-hidden /> Agregar
        </button>
      </form>
    </div>
  );
}
